import axios from './api';
import { toast } from 'react-toastify';

/**
 * Upload file to server
 * @param {File} file - The file to upload
 * @param {Function} onProgress - Callback for upload progress (0-100)
 * @param {Object} options - Additional options
 * @returns {Promise<string>} - URL of uploaded file
 */
export const uploadFile = async (file, onProgress = null, options = {}) => {
  const {
    maxSize = 5 * 1024 * 1024, // 5MB default
    allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'],
    endpoint = '/upload',
  } = options;

  // Validate file size
  if (file.size > maxSize) {
    const sizeMB = (maxSize / (1024 * 1024)).toFixed(1);
    throw new Error(`File size exceeds ${sizeMB}MB limit`);
  }

  // Validate file type
  if (allowedTypes.length > 0 && !allowedTypes.includes(file.type)) {
    throw new Error(`File type ${file.type} is not allowed`);
  }

  const formData = new FormData();
  formData.append('file', file);

  try {
    const response = await axios.post(endpoint, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(percentCompleted);
        }
      },
    });

    return response.data.url || response.data.path || response.data;
  } catch (error) {
    console.error('File upload error:', error);
    throw error;
  }
};

/**
 * Upload image file with preview
 * @param {File} file - The image file to upload
 * @param {Function} onProgress - Callback for upload progress
 * @returns {Promise<Object>} - Object with url and preview
 */
export const uploadImage = async (file, onProgress = null) => {
  // Validate it's an image
  if (!file.type.startsWith('image/')) {
    throw new Error('File must be an image');
  }

  // Create preview URL
  const preview = URL.createObjectURL(file);

  try {
    const url = await uploadFile(file, onProgress, {
      allowedTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'],
      maxSize: 5 * 1024 * 1024, // 5MB
    });

    return { url, preview };
  } catch (error) {
    URL.revokeObjectURL(preview);
    throw error;
  }
};

/**
 * Convert file to base64 (for preview or small files)
 * @param {File} file - The file to convert
 * @returns {Promise<string>} - Base64 string
 */
export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

/**
 * Validate file before upload
 * @param {File} file - The file to validate
 * @param {Object} options - Validation options
 * @returns {Object} - Validation result { valid: boolean, error: string }
 */
export const validateFile = (file, options = {}) => {
  const {
    // maxSize = 5 * 1024 * 1024,
    allowedTypes = [],
    allowedExtensions = [],
  } = options;

  if (!file) {
    return { valid: false, error: 'No file provided' };
  }

  // Check file size
  // if (file.size > maxSize) {
  //   const sizeMB = (maxSize / (1024 * 1024)).toFixed(1);
  //   return { valid: false, error: `File size exceeds ${sizeMB}MB limit` };
  // }

  // Check file type
  if (allowedTypes.length > 0 && !allowedTypes.includes(file.type)) {
    return { valid: false, error: `File type ${file.type} is not allowed` };
  }

  // Check file extension
  if (allowedExtensions.length > 0) {
    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (!allowedExtensions.includes(fileExtension)) {
      return { valid: false, error: `File extension .${fileExtension} is not allowed` };
    }
  }

  return { valid: true, error: null };
};

/**
 * Format file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} - Formatted size string
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Upload multiple files
 * @param {FileList|Array} files - Files to upload
 * @param {Function} onProgress - Callback for overall progress
 * @param {Object} options - Upload options
 * @returns {Promise<Array>} - Array of upload results
 */
export const uploadMultipleFiles = async (files, onProgress = null, options = {}) => {
  const fileArray = Array.from(files);
  const results = [];
  let completedCount = 0;

  for (const file of fileArray) {
    try {
      const url = await uploadFile(file, null, options);
      results.push({ file: file.name, url, success: true });
    } catch (error) {
      results.push({ file: file.name, error: error.message, success: false });
    }

    completedCount++;
    if (onProgress) {
      onProgress(Math.round((completedCount / fileArray.length) * 100));
    }
  }

  return results;
};

/**
 * Upload with toast notifications
 * @param {File} file - The file to upload
 * @param {Object} options - Upload options
 * @returns {Promise<string>} - URL of uploaded file
 */
export const uploadFileWithToast = async (file, options = {}) => {
  const toastId = toast.loading('Uploading file...');

  try {
    const url = await uploadFile(
      file,
      (progress) => {
        toast.update(toastId, {
          render: `Uploading... ${progress}%`,
          isLoading: true,
        });
      },
      options
    );

    toast.update(toastId, {
      render: 'File uploaded successfully!',
      type: 'success',
      isLoading: false,
      autoClose: 3000,
    });

    return url;
  } catch (error) {
    toast.update(toastId, {
      render: error.message || 'Upload failed',
      type: 'error',
      isLoading: false,
      autoClose: 5000,
    });
    throw error;
  }
};

